import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Edit } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import { useGetVehiclesQuery } from "../vehiclesApi";
import { useCanCreateVehicles } from "../useVehiclePermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import VehicleTypeSelect from "./VehicleTypeSelect";
import { vehicleTypeLabel } from "../vehicleTypes";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// "Basic View" pane of the master-detail split, fourth module on this
// pattern after Employee Master, Customers, and Products (2026-08-29) -
// built on the same reusable MasterDetailListShell/MasterDetailRow.
const VehicleCompactList = () => {
  const canCreate = useCanCreateVehicles();
  const navigate = useNavigate();
  const { vehicleId: selectedVehicleId } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [vehicleType, setVehicleType] = useState("");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: vehicles = [], isLoading } = useGetVehiclesQuery({
    search: search || undefined,
    includeInactive: status === "" || status === "inactive" ? true : undefined,
    branchId: selectedBranchId ?? undefined,
    vehicleType: vehicleType || undefined,
  });

  const filtered =
    status === "" ? vehicles : vehicles.filter((v) => Boolean(v.isActive) === (status === "active"));

  const initials = (v) => (v.regNo || "?").trim().slice(0, 2).toUpperCase();

  return (
    <MasterDetailListShell
      title="Vehicles"
      addButton={
        canCreate && (
          <button type="button" className="btn btn-added" onClick={() => navigate(paths.vehicleNew)}>
            <PlusCircle className="me-2" size={16} />
            Add New Vehicle
          </button>
        )
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search reg no, brand, or model"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="mt-2 d-flex gap-2">
            <VehicleTypeSelect className="form-control form-control-sm" value={vehicleType} onChange={setVehicleType} allLabel="All Types" />
            <select className="form-control form-control-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      }
      isLoading={isLoading}
      emptyLabel="No vehicles match these filters."
      items={filtered}
      getKey={(v) => v.vehicleId}
      resetKey={`${search}|${status}|${vehicleType}|${selectedBranchId}`}
      renderItem={(v) => {
        const isSelected = String(v.vehicleId) === String(selectedVehicleId);
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.vehicleEdit(v.vehicleId))}
            avatarLabel={initials(v)}
            title={<>{v.regNo || "-"}</>}
            subtitle={
              <>
                {[v.brand, v.model].filter(Boolean).join(" ") || "-"} &middot;{" "}
                {vehicleTypeLabel(v.vehicleType)}
                {v.allocatedToEmployeeName ? ` \u00b7 ${v.allocatedToEmployeeName}` : ""}
              </>
            }
            badge={
              <span className={`badge ${v.isActive ? "badge-linesuccess" : "badge-linedanger"} me-2`}>
                {v.isActive ? "Active" : "Not In Use"}
              </span>
            }
            actions={
              <button
                type="button"
                className="btn btn-link p-1"
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(paths.vehicleEdit(v.vehicleId));
                }}
              >
                <Edit size={15} />
              </button>
            }
          />
        );
      }}
    />
  );
};

export default VehicleCompactList;
